import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import { formatDistanceToNow } from 'date-fns';
import { CircleNotchIcon } from '@phosphor-icons/react';
import type { WorkflowRunResponse } from '@erp/rpc';
import { Badge } from 'src/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/components/ui/hover-card';
import paths from 'src/paths';
import {
  useGetWorkflowRun,
  useListRerunsForRun,
} from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import {
  calculateRunDurationMs,
  formatRunDurationSeconds,
  getRunStatusVariant,
  RunStatusIcon,
} from './runStatusDisplay';

const HOVER_OPEN_DELAY_MS = 200;
const HOVER_CLOSE_DELAY_MS = 150;

function stopRowClick(event: React.MouseEvent) {
  event.stopPropagation();
}

function RelativeTime({ iso }: { iso: string }) {
  return (
    <span title={new Date(iso).toLocaleString()}>
      {formatDistanceToNow(new Date(iso), { addSuffix: true })}
    </span>
  );
}

/**
 * Compact summary of a single run for hover cards: status, when and by whom it
 * was triggered, how long it took, and why it stopped.
 */
export function RunSummaryCard({
  run,
  workflowId,
}: {
  run: WorkflowRunResponse;
  workflowId: string;
}) {
  const wallTimeMs =
    run.wallTimeMs ?? calculateRunDurationMs(run.startedAt, run.completedAt);

  return (
    <div className="space-y-1.5 text-xs">
      <div className="flex items-center gap-2">
        <Badge variant={getRunStatusVariant(run.status)}>{run.status}</Badge>
        {run.workflowDefinitionVersion !== undefined && (
          <span className="text-muted-foreground">
            <T context="workflow version label in run hover card">
              v<Var>{run.workflowDefinitionVersion}</Var>
            </T>
          </span>
        )}
        <span className="ml-auto text-muted-foreground">
          <RelativeTime iso={run.triggeredAt} />
        </span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        <dt className="text-muted-foreground">
          <T context="run hover card field label">Triggered by</T>
        </dt>
        <dd>
          {run.triggeredByName ?? (
            <T context="workflow run triggered by system label">System</T>
          )}
        </dd>
        <dt className="text-muted-foreground">
          <T context="run hover card field label">Duration</T>
        </dt>
        <dd className="font-mono">
          <T context="run duration in seconds">
            <Var>{formatRunDurationSeconds(wallTimeMs)}</Var>s
          </T>
        </dd>
        <dt className="text-muted-foreground">
          <T context="run hover card field label">Steps</T>
        </dt>
        <dd className="font-mono">
          <Var>{run.completedSteps}</Var>/<Var>{run.totalSteps}</Var>
        </dd>
        {run.stopReason && (
          <>
            <dt className="text-muted-foreground">
              <T context="run hover card field label">Stop reason</T>
            </dt>
            <dd className="text-destructive break-words line-clamp-3">
              {run.stopReason}
            </dd>
          </>
        )}
      </dl>
      <div className="flex items-center justify-between gap-2 border-t pt-1.5">
        <span className="font-mono text-[10px] text-muted-foreground truncate">
          {run.id}
        </span>
        <Link
          to={paths.authenticated.workflows.run}
          params={{ workflowId, workflowRunId: run.id }}
          className="shrink-0 text-primary underline-offset-2 hover:underline"
          onClick={stopRowClick}
        >
          <T context="link to open a workflow run from a hover card">
            View run
          </T>
        </Link>
      </div>
    </div>
  );
}

function HoverLoading() {
  return (
    <div className="flex items-center justify-center py-2">
      <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * "Retried" marker on a run that has been rerun. Hovering shows each rerun's
 * outcome so the user can tell at a glance whether the retry succeeded.
 */
export function RerunsHoverBadge({
  runId,
  workflowId,
}: {
  runId: string;
  workflowId: string;
}) {
  const gt = useGT();
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading, isError } = useListRerunsForRun(runId, {
    enabled: isOpen,
  });
  const reruns = isError ? [] : (data?.reruns ?? []);
  const latest = reruns[0];

  return (
    <HoverCard
      openDelay={HOVER_OPEN_DELAY_MS}
      closeDelay={HOVER_CLOSE_DELAY_MS}
      onOpenChange={setIsOpen}
    >
      <HoverCardTrigger asChild>
        {/* A real button so keyboard users can focus it and open the card. */}
        <button
          type="button"
          className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={stopRowClick}
          aria-label={gt('This run has been rerun; show its reruns', {
            context: 'accessible label for the retried run badge',
          })}
        >
          <Badge variant="outline" className="text-xs cursor-default">
            <T context="workflow run indicator">Retried</T>
          </Badge>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-[22rem] p-2"
        align="start"
        onClick={stopRowClick}
      >
        <div className="mb-1 flex items-center justify-between text-xs font-semibold">
          <T context="workflow run section">Reruns</T>
          {latest && (
            <span className="flex items-center gap-1 font-normal text-muted-foreground">
              <T context="latest rerun outcome in hover card">Latest:</T>
              <RunStatusIcon status={latest.status} />
              <span>{latest.status}</span>
            </span>
          )}
        </div>
        {isLoading && <HoverLoading />}
        {!isLoading && isError && (
          <div className="text-destructive text-xs py-1">
            <T context="workflow reruns error state">Failed to load reruns.</T>
          </div>
        )}
        {!isLoading && !isError && reruns.length === 0 && (
          <div className="text-muted-foreground text-xs py-1">
            <T context="workflow reruns empty state">No reruns found.</T>
          </div>
        )}
        {!isLoading &&
          reruns.map((rerun) => (
            <Link
              key={rerun.id}
              to={paths.authenticated.workflows.run}
              params={{ workflowId, workflowRunId: rerun.id }}
              className="flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-muted/50"
            >
              <Badge
                variant={getRunStatusVariant(rerun.status)}
                className="shrink-0"
              >
                {rerun.status}
              </Badge>
              <span className="font-mono truncate">{rerun.id.slice(0, 8)}</span>
              {rerun.stopReason && (
                <span
                  className="truncate text-destructive"
                  title={rerun.stopReason}
                >
                  {rerun.stopReason}
                </span>
              )}
              <span className="ml-auto whitespace-nowrap text-muted-foreground">
                {rerun.status === 'scheduled' && rerun.scheduledFor ? (
                  <T context="scheduled rerun fire time in hover card">
                    fires{' '}
                    <Var>
                      {formatDistanceToNow(new Date(rerun.scheduledFor), {
                        addSuffix: true,
                      })}
                    </Var>
                  </T>
                ) : (
                  <RelativeTime iso={rerun.triggeredAt} />
                )}
              </span>
            </Link>
          ))}
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * "Rerun" marker on a run that was started from another run. Hovering shows a
 * summary of that original run; clicking navigates to it.
 */
export function RerunOfHoverBadge({
  originalRunId,
  workflowId,
}: {
  originalRunId: string;
  workflowId: string;
}) {
  const gt = useGT();
  const [isOpen, setIsOpen] = useState(false);
  const {
    data: originalRun,
    isLoading,
    isError,
  } = useGetWorkflowRun(isOpen ? originalRunId : undefined);

  return (
    <HoverCard
      openDelay={HOVER_OPEN_DELAY_MS}
      closeDelay={HOVER_CLOSE_DELAY_MS}
      onOpenChange={setIsOpen}
    >
      <HoverCardTrigger asChild>
        <Link
          className="inline-flex"
          to={paths.authenticated.workflows.run}
          params={{ workflowId, workflowRunId: originalRunId }}
          onClick={stopRowClick}
        >
          <Badge
            variant="outline"
            className="text-xs cursor-pointer"
            aria-label={gt('This run is a rerun of another run', {
              context: 'accessible label for the rerun badge',
            })}
          >
            <T context="workflow run indicator">Rerun</T>
          </Badge>
        </Link>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-[22rem] p-2"
        align="start"
        onClick={stopRowClick}
      >
        <div className="mb-1 text-xs font-semibold">
          <T context="hover card heading for the run this rerun was started from">
            Original run
          </T>
        </div>
        {isLoading && <HoverLoading />}
        {!isLoading && (isError || originalRun === null) && (
          <div className="text-muted-foreground text-xs py-1">
            <T context="hover card state when the original run cannot be loaded">
              Original run is no longer available.
            </T>
          </div>
        )}
        {!isLoading && originalRun && (
          <RunSummaryCard run={originalRun} workflowId={workflowId} />
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
